import React, { createContext, useContext, useState } from 'react';
import { LOCAL_DISABLE_ULTRASOUND, LOCAL_FEEDBACK_WINDOW_MIN, LOCAL_HR_RANGE, LOCAL_PLOT_MHR, LOCAL_TEST_GRIDS, LOCAL_TWIN_MODE, LOCAL_TWIN_OFFSET } from '../utils/constants';

const SettingsContext = createContext();

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

export const SettingsProvider = ({ children }) => {
    const [hrRange, setHrRange] = useState(() => localStorage.getItem(LOCAL_HR_RANGE) || '60-200');
    const [plotMhr, setPlotMhr] = useState(() => localStorage.getItem(LOCAL_PLOT_MHR) !== 'false');
    const [disableUltrasound, setDisableUltrasound] = useState(() => localStorage.getItem(LOCAL_DISABLE_ULTRASOUND) === 'true');
    const [testGrids, setTestGrids] = useState(() => Number(localStorage.getItem(LOCAL_TEST_GRIDS) || 4));
    const [feedbackWindowMin, setFeedbackWindowMin] = useState(() => {
        const v = Number(localStorage.getItem(LOCAL_FEEDBACK_WINDOW_MIN) || 5);
        if (v < 2 || v > 20 || Number.isNaN(v)) return 5;
        return v;
    });

    const [twinMode, setTwinMode] = useState(() => localStorage.getItem(LOCAL_TWIN_MODE) || 'twin_mode_simultaneous');
    const [twinOffset, setTwinOffset] = useState(() => Number(localStorage.getItem(LOCAL_TWIN_OFFSET) || -20));

    const updateSettings = (newSettings) => {
        if (newSettings.hrRange !== undefined) {
            setHrRange(newSettings.hrRange);
            localStorage.setItem(LOCAL_HR_RANGE, newSettings.hrRange);
        }
        if (newSettings.plotMhr !== undefined) {
            setPlotMhr(newSettings.plotMhr);
            localStorage.setItem(LOCAL_PLOT_MHR, newSettings.plotMhr ? 'true' : 'false');
        }
        if (newSettings.disableUltrasound !== undefined) {
            setDisableUltrasound(newSettings.disableUltrasound);
            localStorage.setItem(LOCAL_DISABLE_ULTRASOUND, newSettings.disableUltrasound ? 'true' : 'false');
        }
        if (newSettings.testGrids !== undefined) {
            setTestGrids(Number(newSettings.testGrids));
            localStorage.setItem(LOCAL_TEST_GRIDS, newSettings.testGrids);
        }
        if (newSettings.feedbackWindowMin !== undefined) {
            const n = Number(newSettings.feedbackWindowMin);
            const clamped = n < 2 ? 2 : n > 20 ? 20 : n;
            setFeedbackWindowMin(clamped);
            localStorage.setItem(LOCAL_FEEDBACK_WINDOW_MIN, String(clamped));
        }
        if (newSettings.twinMode !== undefined) {
            setTwinMode(newSettings.twinMode);
            localStorage.setItem(LOCAL_TWIN_MODE, newSettings.twinMode);
        }
        if (newSettings.twinOffset !== undefined) {
            setTwinOffset(Number(newSettings.twinOffset));
            localStorage.setItem(LOCAL_TWIN_OFFSET, String(newSettings.twinOffset));
        }
        
        // Still dispatching the event for non-react components or legacy listeners
        window.dispatchEvent(new CustomEvent('app-settings-changed'));
    };

    const value = {
        hrRange,
        plotMhr,
        disableUltrasound,
        testGrids,
        feedbackWindowMin,
        twinMode,
        twinOffset,
        updateSettings
    };

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};
